import { Button } from '@ce-cfbdigital/cgl-cfb-fe-foundation';
import DOMPurify from 'dompurify';
import PropTypes from 'prop-types';

import CustomIcon from '../CustomIcons';
import useFooter from './customHook';

import './styles/index.css';

const currentYear = new Date().getFullYear();

const Footer = () => {
  const {
    config: { footer, labels, tableData },
    isLoading,
    expand,
    showDetails,
    toggleDetailsHandler,
    setAnalytics
  } = useFooter();

  const { moreDetails, close, copyright } = labels;

  const setInnerHTML = item => {
    if (item.addExtra === 'yes') {
      return <span id="purchase-order">{item.label}</span>;
    }
    if (item.addExtra === 'image') {
      return <img className="logo-img" alt={item.label} src={item.src} />;
    }
    return item.label;
  };

  const getMoreDetails = () => (
    <ul className="footer-link-group">
      <li className="footer-more-details">
        {!isLoading ? (
          <div role="button" tabIndex={0} onClick={toggleDetailsHandler} onKeyDown={toggleDetailsHandler}>
            <span>{moreDetails}</span>
            {expand ? (
              <CustomIcon type="downArrow" className="icon-display" size="20" />
            ) : (
              <CustomIcon type="upArrow" className="icon-display" size="20" />
            )}
          </div>
        ) : (
          <TypingLoader />
        )}
      </li>
    </ul>
  );

  const getColumn = (columnData, target = '_blank') => {
    const { rowData, title } = columnData;
    const isInternal = !target;
    return (
      <div className="column">
        <h2>{title}</h2>
        <ul>
          {rowData.map((row, index) => (
            <li key={index}>
              {isInternal ? (
                <Button classes="embedded-links" handleClick={() => setAnalytics(title, isInternal, row)}>
                  {row.label}
                </Button>
              ) : (
                <a
                  href={DOMPurify.sanitize(row.link) || '#'}
                  target={target}
                  rel="noopener noreferrer"
                  onClick={() => setAnalytics(title, isInternal, row)}
                >
                  {row.label}
                </a>
              )}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const getDetailsWrapper = () => {
    const {
      headers: { corporatePolicy, news, interested }
    } = tableData;
    return (
      <div className={`details-wrapper ${expand ? 'show-panel' : ''}`}>
        {getColumn(corporatePolicy)}
        {getColumn(news)}
        {getColumn(interested, '')}
        <div
          className="close-icon"
          role="button"
          tabIndex={0}
          onClick={toggleDetailsHandler}
          onKeyDown={toggleDetailsHandler}
          title={close}
          aria-label="close"
        >
          <CustomIcon size="24" type="mdClose" />
        </div>
      </div>
    );
  };

  const getLink = item => {
    const { src, target, label } = item;
    return (
      <a href={DOMPurify.sanitize(src)} target={target} rel="noreferrer" aria-label={label}>
        {setInnerHTML(item)}
      </a>
    );
  };

  const TypingLoader = () => (
    <div className="dot-typing-wrapper">
      <div className="dot-typing" />
    </div>
  );

  return (
    <div className="footer-container">
      {!isLoading && getDetailsWrapper()}
      <footer>
        <div className="footer-links">
          {!isLoading && footer ? (
            footer.map((item, key) => (
              <ul className="footer-link-group" key={key}>
                <li>{getLink(item)}</li>
              </ul>
            ))
          ) : (
            <ul className="footer-link-group">
              <TypingLoader />
            </ul>
          )}
          <ul className="footer-link-group">
            <li>
              <span id="teconsent" className="footer-consent-link" />
            </li>
          </ul>
          <ul className="footer-link-group">
            <li className="nav-item">
              <span id="trustarc-irm-placeholder" />
            </li>
          </ul>
          {showDetails && getMoreDetails()}
        </div>
        <div className="footer-copyright">
          <p>
            &copy; {currentYear} {copyright}
          </p>
        </div>
      </footer>
    </div>
  );
};

Footer.propTypes = {
  config: PropTypes.shape({
    footer: PropTypes.instanceOf(Array),
    labels: PropTypes.shape({
      moreDetails: PropTypes.string,
      close: PropTypes.string,
      copyright: PropTypes.string
    }),
    tableData: PropTypes.shape({
      headers: PropTypes.shape({
        corporatePolicy: PropTypes.string,
        news: PropTypes.string,
        interested: PropTypes.string
      })
    })
  })
};

Footer.defaultProps = {
  config: {
    footer: [],
    labels: {
      moreDetails: 'More On This Site',
      close: 'Close',
      copyright: 'Cargill Incorporated. All Right Reserved.'
    },
    tableData: {
      headers: {
        corporatePolicy: '',
        news: '',
        interested: ''
      }
    }
  }
};

export default Footer;
